import {allRouteDefs} from 'common/src/modules/shared/routing';
import {accountPrefix} from '../routing.consts';
import type {AppRouteParams, AppRouteQueryParams, RouteWithParams} from '../routing.types';
import getRouteKey from './getRouteKey';
import parsePathParam from './parsePathParam';
import parseQueryParam from './parseQueryParam';

function getRouteWithParamsFromUrl(url: string): RouteWithParams | null {
  const [pathname, search = ''] = url.split('?');
  const routeKey = getRouteKey(pathname);

  if (!routeKey) {
    return null;
  }

  const accountPrefixPattern = accountPrefix.replace(':accountId', '\\d+');
  const accountPrefixRegex = new RegExp(`^${accountPrefixPattern}`);
  const normalizedPath = pathname.replace(accountPrefixRegex, '');

  const routeDef = allRouteDefs[routeKey];

  if (!routeDef) {
    return {
      routeKey,
      pathParamValues: {},
      queryParamValues: {},
    };
  }

  const routeParts = routeDef.path.split('/').slice(1);
  const pathParts = normalizedPath.split('/').slice(1);
  const rawPathParams: Record<string, string> = {};

  for (let i = 0; i < routeParts.length; i++) {
    const routePart = routeParts[i];
    const pathPart = pathParts[i];

    if (routePart.startsWith(':')) {
      rawPathParams[routePart.substring(1)] = pathPart;
    }
  }

  const pathParamValues: AppRouteParams = {};

  for (const pathParamDef of routeDef.pathParams) {
    const rawValue = rawPathParams[pathParamDef.key];

    if (rawValue !== undefined) {
      try {
        pathParamValues[pathParamDef.key] = parsePathParam(rawValue, pathParamDef);
      } catch (error) {
        console.warn(`Error parsing path parameter ${pathParamDef.key}:`, error);
        pathParamValues[pathParamDef.key] = rawValue;
      }
    } else if (pathParamDef.defaultValue) {
      pathParamValues[pathParamDef.key] = pathParamDef.defaultValue;
    }
  }

  const urlSearchParams = new URLSearchParams(search);
  const rawQueryParams: Record<string, string | string[]> = {};

  for (const [key, value] of urlSearchParams.entries()) {
    if (rawQueryParams[key]) {
      if (Array.isArray(rawQueryParams[key])) {
        (rawQueryParams[key] as string[]).push(value);
      } else {
        rawQueryParams[key] = [rawQueryParams[key] as string, value];
      }
    } else {
      rawQueryParams[key] = value;
    }
  }

  const queryParamValues: AppRouteQueryParams = {};

  for (const queryParamDef of routeDef.queryParams) {
    const rawValue = rawQueryParams[queryParamDef.key];

    if (rawValue !== undefined) {
      try {
        queryParamValues[queryParamDef.key] = parseQueryParam(rawValue, queryParamDef);
      } catch (error) {
        console.warn(`Error parsing query parameter ${queryParamDef.key}:`, error);
        queryParamValues[queryParamDef.key] = Array.isArray(rawValue) ? rawValue[0] : rawValue;
      }
    } else if (queryParamDef.defaultValue !== undefined) {
      queryParamValues[queryParamDef.key] = queryParamDef.defaultValue;
    }
  }

  const definedParamKeys = new Set(routeDef.queryParams.map((def) => def.key));

  for (const [key, rawValue] of Object.entries(rawQueryParams)) {
    if (!definedParamKeys.has(key)) {
      queryParamValues[key] = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    }
  }

  return {
    routeKey,
    pathParamValues,
    queryParamValues,
  };
}

export default getRouteWithParamsFromUrl;
